<template>
  <div data-app>
    <div
      role="alert"
      v-bind:class="{ show: errors.length }"
      class="alert fade alert-danger"
    >
      <div class="alert-text" v-for="(error, i) in errors" :key="i">
        {{ error }}
      </div>
    </div>
    <v-card>
      <v-card-title>{{ website_name }} Page Security Scripts</v-card-title>
      <v-card-text>
        <v-btn color="primary" dark class="mb-2" @click="exportToCsv">
          <v-icon left>mdi-download</v-icon>
          Download CSV
        </v-btn>
      </v-card-text>
      <v-data-table
        v-model="selected"
        :loading="loading"
        :headers="headers"
        :items="scripts"
        :footer-props="{
          itemsPerPageOptions: [10, 20, 50, 100],
          showFirstLastPage: true,
          firstIcon: 'mdi-arrow-collapse-left',
          lastIcon: 'mdi-arrow-collapse-right',
          prevIcon: 'mdi-chevron-left',
          nextIcon: 'mdi-chevron-right',
        }"
        :items-per-page="itemsPerPage"
        :server-items-length="totalScripts"
        @update:page="onPageChange"
        @update:items-per-page="onItemsPerPageChange"
        item-key="id"
        class="elevation-1"
      >
        <template v-slot:item.history="{ item }">
          <v-btn small :to="`/page_security_scripts_history/${item.id}`">
            View History
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import api from "@/common/api.service";
import Errors from "@/common/errors";
import { jsonToCsv, downloadCsv } from "@/common/json-to-csv";

export default {
  mounted() {
    this.fetchScripts();
    this.website_name = this.$store.state.web.website_name;
  },

  data: () => ({
    path: "",
    selected: [],
    headers: [
      { text: "ID", sortable: false, align: "left", value: "id" },
      { text: "URL", value: "url" },
      { text: "Host", value: "host" },
      { text: "Page URLs", value: "page_urls" },
      { text: "Hash", value: "hash" },
      { text: "Created At", value: "created_at" },
      { text: "Updated At", value: "updated_at" },
      { text: "Last Seen At", value: "last_seen_at" },
      { text: "History", value: "history", sortable: false },
    ],
    scripts: [],
    errors: [],
    loading: true,
    search: "",
    website_name: "",
    itemsPerPage: 10,
    totalScripts: 0,
    currentPage: 1,
    searchCounter: 0,
  }),

  watch: {
    dialog(val) {
      val || this.close();
    },
  },

  methods: {
    fetchScripts() {
      this.searchCounter++;
      const currentRequest = this.searchCounter;

      this.loading = true;
      const queryParams = new URLSearchParams();
      queryParams.append("page", this.currentPage);
      queryParams.append("size", this.itemsPerPage);
      queryParams.append("customer_website_id", this.$route.params.website_id);

      api
        .query(`v2/waf/page_security/scripts?${queryParams.toString()}`)
        .then((response) => {
          if (this.searchCounter == currentRequest) {
            this.scripts = response.data.data.map((script) => ({
              ...script,
              page_urls: script.page_urls.join(", "),
              created_at: new Date(script.created_at).toLocaleString(),
              updated_at: new Date(script.updated_at).toLocaleString(),
              last_seen_at: new Date(script.last_seen_at).toLocaleString(),
            }));
            this.totalScripts = response.data.total;
          }
        })
        .catch((error) =>
          this.errors.push(...Errors.extractErrorMessage(error))
        )
        .finally(() => {
          this.loading = false;
        });
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchScripts();
    },

    onItemsPerPageChange(size) {
      this.itemsPerPage = size;
      this.currentPage = 1; // Reset to the first page
      this.fetchScripts();
    },

    async exportToCsv() {
      try {
        const totalPages = Math.ceil(this.totalScripts / 100);
        const promises = [];

        // Create array of promises for all API calls
        for (let page = 1; page <= totalPages; page++) {
          const queryParams = new URLSearchParams();
          queryParams.append("page", page);
          queryParams.append("size", 100);
          queryParams.append(
            "customer_website_id",
            this.$route.params.website_id
          );

          promises.push(
            api.query(`v2/waf/page_security/scripts?${queryParams.toString()}`)
          );
        }

        // Wait for all API calls to complete
        const responses = await Promise.all(promises);

        // Process all the responses
        const scripts = responses.flatMap((response) => {
          if (response.data && response.data.data) {
            return response.data.data.map(({history, ...script}) => ({
              ...script,
              page_urls: Array.isArray(script.page_urls)
                ? script.page_urls.join(", ")
                : "",
              created_at: new Date(script.created_at).toLocaleString(),
              updated_at: new Date(script.updated_at).toLocaleString(),
              last_seen_at: new Date(script.last_seen_at).toLocaleString(),
            }));
          }
          return [];
        });

        const csv = jsonToCsv(scripts);
        downloadCsv(csv, this.website_name + "_scripts.csv");
      } catch (error) {
        console.error("Error exporting to CSV:", error);
        this.errors.push("Failed to export data to CSV");
      }
    },
  },
};
</script>
